import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, IconButton, Tabs, useMediaQuery, useTheme, Button } from '@mui/material';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import TabContext from '@mui/lab/TabContext';
import { Icon } from '@iconify/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useEventDetailsStyles from './useEventDetailsStyle';
import { PageLayout } from '@ui/components/PageLayout';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import TabWithBadge from '@ui/components/TabWithBadge';
import { Copy, Link } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Edit } from 'react-iconly';
import AttachmentsTab from './AttachmentsTab';
import DetailsTab from './DetailsTab';
import TasksTab from './TasksTab';
import InterestsTab from './InterestsTab';
import TranslationsTab from './TranslationsTab';
import RSVPTrackingTab from './MVPTrackingTab';
import PreferencesTab from './PreferencesTab';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { FileErrorType } from '@application/enums/file-error-type';
import { createEventAttachmentAsync, getAllEventAttachmentsAsync } from '@application/features/vdkEventDocument/vdkEventDocumentThunk';
import { FileUploadProvider, useFileUploadOptional } from '@ui/contexts/FileUploadContext';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { hasPermission } from '@application/helpers/permission-helper';
import DashboardTab from './DashboardTab';
import FamilyFestDashboardTab from './FamilyFestDashboard';
import { ResponsesTab } from './ResponsesTab';
import { getAllActiveResponsesByEventIdGroupedByRegistrationAsync } from '@application/features/vdkEventResponses/vdkEventResponsesThunk';
import { getVDKEventByIdForEventDetailsAsync, updateVDKEventLinkAndPasswordAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { getFamilyFestAttendeesCountAsync } from '@application/features/familyFest/familyFestAttendeesThunk';
import { EventDocumentType } from '@application/enums/event-document-type';
import CustomDialog from '@ui/components/common/Dialog';
import { CheckOutlined } from '@mui/icons-material';
import { SingleSelect } from '@ui/components/common/Autocomplete';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import { HDateRangePicker } from '@ui/components/common/Pickers';
import FormValidationMessage from '@ui/components/FormValidationMessage';
import HSingleDatePicker from '@ui/components/common/Pickers/HDatePicker';
import dayjs from 'dayjs';
import { formatDateForDatePicker } from '@application/utilities/date-utils';
import { getAllUserCompaniesAsync } from '@application/features/company/companyThunk';
import ControlledHCheckboxAutocomplete from '@ui/components/common/Autocomplete/ControlledHCheckboxAutocomplete';
import BDrawer from '@ui/components/common/BDrawer';
import { useHelpTopic } from '@ui/contexts';
import TransportTab from './TransportTab';
import LocationDetail from './TransportTab/LocationContent/LocationDetail';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import FaqsTab from './FaqsTab';
import FaqDetailsContent from './FaqsTab/FaqsContent/FaqsDetails';
import { getFaqCategoriesByEventIdAsync } from '@application/features/faqCategory/faqCategoryThunk';
import { getFamilyFestFaqCategoriesAsync } from '@application/features/familyFestFaqCategory/familyFestFaqCategoryThunk';
import { deleteFaqQuestionAsync } from '@application/features/faqQuestion/faqQuestionThunk';
import { updateFaqQuestionAsync } from '@application/features/faqQuestion/faqQuestionThunk';
import { deleteFamilyFestFaqQuestionAsync, updateFamilyFestFaqQuestionAsync } from '@application/features/familyFestFaqQuestion/familyFestFaqQuestionThunk';
import AccommodationTab from './AccommodationTab';
import EventPageTab from './EventPageTab';
const STATIC_FAMILY_FEST_CAPACITY_PROGRESS = {
    current: 8500,
    max: 17000
};
const EventDetailsContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { control } = useForm({
        defaultValues: {
            myFiles: []
        }
    });
    // Dialog state and form for event link and password
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { control: dialogControl, setValue: setDialogValue, handleSubmit: handleDialogSubmit, formState: { errors: dialogErrors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            companies: [],
            assignTo: '',
            externalsAllowed: true,
            noOfSteps: '5',
            details: '',
            floorPlan: '',
            passwordProtection: false,
            password: '',
            backgroundImage: '',
            eventDuration: { startDate: '', endDate: '' },
            eventLinkExpiryDate: '',
            eventLink: ''
        }
    });
    const passwordProtection = useWatch({ control: dialogControl, name: 'passwordProtection' });
    const isPasswordProtected = passwordProtection === true;
    const { eventType } = useSelector((state) => state.f1Integration);
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const currentUser = useSelector((state) => state.auth.currentUser);
    const isCollaborator = Array.isArray(currentUserRoles) && currentUserRoles.includes('Collaborator');
    const dispatch = useAppDispatch();
    const detailsTabRef = useRef(null);
    const navigate = useNavigate();
    const { eventId, type } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { VDKEvents, VDKEventsForEventDetails, status } = useAppSelector((state) => state.VDKEvent);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const isTransportManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('TransportManager');
    const isHappinessManager = Array.isArray(currentUserRoles) && currentUserRoles.includes('HappinessManager');
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const isExternalTransportManager = isTransportManager &&
        Number(currentUser?.CollaboratorAccessLevel) === CollaboratorAccessLevel.External;
    // Dialog-related data
    const { companies } = useSelector((state) => state.company);
    const { usersByCompany: users } = useSelector((state) => state.user);
    // Check if mobile screen
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    // Load companies on component mount
    useEffect(() => {
        dispatch(getAllUserCompaniesAsync());
    }, [dispatch]);
    // Transform companies for dropdown
    const companyOptions = companies.map((company) => ({
        label: company.name,
        value: company.id
    }));
    const userOptions = useMemo(() => users.map((user) => ({
        label: user.fullName,
        value: user.id
    })), [users]);
    const passwordProtectionOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ];
    const externalsAllowedOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ];
    // Get responses data to access total count
    const { allResponsesByEventId } = useAppSelector((state) => state.vdkEventResponse);
    // Use VDKEventsForEventDetails if available, otherwise fall back to VDKEvents
    const currentEvent = VDKEventsForEventDetails || (VDKEvents ?? []).find((e) => e.id === eventId);
    const contextValue = useFileUploadOptional();
    const eventTitle = currentEvent?.title ?? 'Event Details';
    const isFamilyEvent = currentEvent?.type === EventTypeEnum.Family;
    const [localFileError, setLocalFileError] = useState({
        hasError: false,
        errorType: FileErrorType.NoError
    });
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canReadTasks = hasPermission(loggedInUserPermissions, 'Permissions.VdkTasks.Read');
    const canReadEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Read');
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const canUpdateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Update');
    const canDeleteEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Delete');
    const [anchorMoreOptionEl, setAnchorMoreOptionEl] = useState(null);
    const handleFilesSubmitted = async (files, title, type) => {
        if (!eventId) {
            return;
        }
        try {
            for (const file of files) {
                await dispatch(createEventAttachmentAsync({
                    eventId: eventId,
                    fileTitle: title,
                    type: type,
                    file
                })).unwrap();
            }
            toast.success(t('events.eventDetails.attachmentsTabs.attachmentAddedSuccessfully'));
        }
        catch (error) {
            toast.error(error?.error || t('events.eventDetails.attachmentsTabs.failedToAddAttachment'));
        }
        finally {
            dispatch(getAllEventAttachmentsAsync({ eventId, pageNumber: 1, pageSize: 1000 }));
        }
    };
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [activeTab, setActiveTab] = useState('details');
    const [familyFestResponsesCount, setFamilyFestResponsesCount] = useState(0);
    const [selectedFaqDetails, setSelectedFaqDetails] = useState(null);
    const isFaqDetailsView = !!selectedFaqDetails;
    const [emailPreferencesView, setEmailPreferencesView] = useState({ mode: 'list', template: null });
    const isEmailPreferencesSubView = emailPreferencesView.mode !== 'list';
    useHelpTopic(isFaqDetailsView
        ? 'faqsDetails'
        : isEmailPreferencesSubView
            ? emailPreferencesView.mode === 'configure'
                ? 'emailConfigurationBuilder'
                : 'emailConfigurationSettings'
            : isFamilyEvent && ['dashboard', 'responses', 'transport'].includes(activeTab)
                ? `${activeTab}FamilyFest`
                : activeTab);
    // Load responses data when component mounts or selected companies change
    useEffect(() => {
        if (eventId) {
            const request = {
                eventId,
                pageNumber: 1,
                pageSize: 10
            };
            dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(request));
        }
    }, [eventId, dispatch, selectedCompanyIds]);
    const refreshFamilyFestCount = useCallback(async () => {
        if (!eventId || !isFamilyEvent) {
            setFamilyFestResponsesCount(0);
            return;
        }
        try {
            const count = await dispatch(getFamilyFestAttendeesCountAsync({ eventId, req: { isArchived: false, isComingToEvent: true } })).unwrap();
            setFamilyFestResponsesCount(count);
        }
        catch {
            setFamilyFestResponsesCount(0);
        }
    }, [eventId, isFamilyEvent, dispatch]);
    useEffect(() => {
        void refreshFamilyFestCount();
    }, [refreshFamilyFestCount, selectedCompanyIds]);
    useEffect(() => {
        if (activeTab === 'responses' && isFamilyEvent) {
            void refreshFamilyFestCount();
        }
    }, [activeTab, isFamilyEvent, refreshFamilyFestCount]);
    useEffect(() => {
        if (!VDKEventsForEventDetails || !selectedCompanyIds?.length)
            return;
        const eventCompanyIds = VDKEventsForEventDetails?.eventCompanies?.map((c) => c.value) || [];
        const hasAccess = selectedCompanyIds.some((id) => eventCompanyIds.includes(id));
        if (!hasAccess) {
            navigate('/events');
        }
    }, [selectedCompanyIds, navigate]);
    // Fetch event details when component mounts
    // useEffect(() => {
    //   if (eventId) {
    //     dispatch(
    //       getVDKEventByIdForEventDetailsAsync({
    //         id: eventId,
    //         documentType: EventDocumentType.BackgroundImage
    //       })
    //     );
    //   }
    // }, [dispatch, eventId]);
    const tabTypeNumber = Number(type);
    const locationId = searchParams.get('locationId');
    const canViewAccommodationTab = isFamilyEvent || tabTypeNumber === 1;
    const restrictedTabsForExternalTransportManager = [
        'responses',
        'rsvpTracking',
        'translations',
        'interests',
        'emailConfiguration',
        'faqs'
    ];
    const restrictedTabsForFamily = ['translations', 'interests', 'emailConfiguration'];
    const hiddenTabsForFamilyFest = ['rsvpTracking'];
    const restrictedTabsForSupplierManager = ['transport'];
    const baseTabs = [
        {
            label: t('events.eventDetails.generalInfo'),
            value: 'details',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.dashboard'),
            value: 'dashboard',
            disabled: !canReadEvents
        },
        //   label: t('events.eventDetails.invitations'),
        //   value: 'invitations',
        //   disabled: !canReadEvents
        // },
        {
            label: t('events.eventDetails.responses'),
            value: 'responses',
            disabled: !canReadEvents,
            count: isFamilyEvent
                ? familyFestResponsesCount
                : allResponsesByEventId?.totalRegistrationsCount
        },
        {
            label: t('events.eventDetails.rsvpTracking.title'),
            value: 'rsvpTracking',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.attachments'),
            value: 'attachments',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.tasks'),
            value: 'tasks',
            disabled: !canReadTasks
        },
        {
            label: t('events.eventDetails.translations'),
            value: 'translations',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.tags'),
            value: 'interests',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.emailConfiguration'),
            value: 'emailConfiguration',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.transport'),
            value: 'transport',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.accommodationTab.accommodation'),
            value: 'accommodation',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.faqs'),
            value: 'faqs',
            disabled: !canReadEvents
        },
        {
            label: t('events.eventDetails.eventPageConfiguration'),
            value: 'eventPage',
            disabled: !canReadEvents
        }
    ];
    const tabLabels = baseTabs.filter((tab) => {
        if (tab.value === 'accommodation' && !canViewAccommodationTab) {
            return false;
        }
        if (tab.value === 'eventPage' && (!isFamilyEvent || !isHappinessManager)) {
            return false;
        }
        // FamilyFest-hidden tabs must stay hidden for every role, including the early-return branches below.
        if (isFamilyEvent &&
            (hiddenTabsForFamilyFest.includes(tab.value) || restrictedTabsForFamily.includes(tab.value))) {
            return false;
        }
        if (isExternalTransportManager) {
            return !restrictedTabsForExternalTransportManager.includes(tab.value);
        }
        if (isCollaborator) {
            return !restrictedTabsForSupplierManager.includes(tab.value);
        }
        if (isFamilyEvent) {
            if (isSfeermeester && (tab.value === 'faqs' || tab.value === 'accommodation')) {
                return false;
            }
            if (isTransportManager && (tab.value === 'faqs')) {
                return false;
            }
        }
        return true;
    });
    const breadcrumbs = useMemo(() => locationId != null
        ? [
            { label: t('common.events'), path: '/events' },
            {
                label: t('events.createNewEvent.eventDetails'),
                onClick: () => {
                    if (!eventId)
                        return;
                    const next = new URLSearchParams(searchParams);
                    next.delete('locationId');
                    setSearchParams(next, { replace: true });
                }
            },
            {
                label: t('events.eventDetails.transportTab.pickupLocation') || 'Pickup Location'
            }
        ]
        : isFaqDetailsView
            ? [
                { label: t('common.events'), path: '/events' },
                {
                    label: t('events.createNewEvent.eventDetails'),
                    onClick: () => setSelectedFaqDetails(null)
                },
                { label: t('events.eventDetails.faqTab.faqDetails') || 'FAQ Details' }
            ]
            : isEmailPreferencesSubView
                ? [
                    { label: t('common.events'), path: '/events' },
                    {
                        label: t('events.createNewEvent.eventDetails'),
                        onClick: () => setEmailPreferencesView({ mode: 'list', template: null })
                    },
                    {
                        label: emailPreferencesView.mode === 'settings'
                            ? t('events.eventDetails.preferencesTab.emailSettings')
                            : t('events.eventDetails.preferencesTab.configureEmail')
                    }
                ]
                : [
                    { label: t('common.events'), path: '/events' },
                    { label: t('events.createNewEvent.eventDetails') }
                ], [
        emailPreferencesView.mode,
        eventId,
        isEmailPreferencesSubView,
        isFaqDetailsView,
        locationId,
        searchParams,
        setSearchParams,
        t
    ]);
    useEffect(() => {
        // Check if tab is specified in URL search params
        const tabFromUrl = searchParams.get('tab');
        const validTabs = [
            'details',
            'dashboard',
            'responses',
            'rsvpTracking',
            'attachments',
            'tasks',
            'translations',
            'interests',
            'emailConfiguration',
            'transport',
            'faqs'
        ];
        if (canViewAccommodationTab) {
            validTabs.push('accommodation');
        }
        if (isFamilyEvent) {
            validTabs.push('eventPage');
        }
        if (isFamilyEvent) {
            for (const hiddenTab of [...hiddenTabsForFamilyFest, ...restrictedTabsForFamily]) {
                const index = validTabs.indexOf(hiddenTab);
                if (index >= 0)
                    validTabs.splice(index, 1);
            }
        }
        if (tabFromUrl && validTabs.includes(tabFromUrl)) {
            setActiveTab(tabFromUrl);
        }
        else {
            setActiveTab('details');
        }
    }, [canViewAccommodationTab, isFamilyEvent, searchParams]);
    useEffect(() => {
        if (activeTab !== 'emailConfiguration' && isEmailPreferencesSubView) {
            setEmailPreferencesView({ mode: 'list', template: null });
        }
    }, [activeTab, isEmailPreferencesSubView]);
    const styles = useEventDetailsStyles(activeTab);
    const exportMenu = [
        {
            text: t('events.eventDetails.dashboard'),
            group: true
        },
        {
            text: t('events.eventDetails.responses'),
            group: true
        },
        {
            text: t('events.eventDetails.tasks'),
            group: true
        },
        {
            text: t('events.eventDetails.tickets'),
            group: true
        },
        {
            text: t('events.eventDetails.exportAll')
        }
    ]; // Magic link copy functionality
    const handleCopyMagicLink = async () => {
        if (currentEvent?.eventURl) {
            await navigator.clipboard.writeText(currentEvent.eventURl);
            toast.success(t('common.magiclinkCopied'));
        }
        else {
            toast.error('Event data not loaded yet. Please wait a moment and try again.');
        }
    };
    // Dialog handlers
    const handleTogglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };
    const handleEditClick = (fieldKey) => {
        setEditingField(fieldKey);
        // Populate form with existing data when editing Event Link & Password
        if (fieldKey === 'labels.eventLinkAndPassword' && VDKEventsForEventDetails) {
            setDialogValue('eventLink', VDKEventsForEventDetails.eventURl || '');
            setDialogValue('eventLinkExpiryDate', VDKEventsForEventDetails.eventURLExpiryDate
                ? formatDateForDatePicker(VDKEventsForEventDetails.eventURLExpiryDate)
                : '');
            setDialogValue('passwordProtection', VDKEventsForEventDetails.isPasswordProtection);
            setDialogValue('password', VDKEventsForEventDetails.password || '');
        }
        setOpenEditDialog(true);
    };
    const handleCopyEventLink = async () => {
        if (VDKEventsForEventDetails?.eventURl) {
            await navigator.clipboard.writeText(VDKEventsForEventDetails.eventURl);
            toast.success(t('common.eventLinkCopied'));
        }
        else {
            toast.error('Event link not available');
        }
    };
    const handleCloseDialog = () => {
        setOpenEditDialog(false);
    };
    const onDialogSubmit = async (data) => {
        if (editingField === 'labels.eventLinkAndPassword') {
            try {
                setIsSubmitting(true);
                const updateData = {
                    id: eventId,
                    eventURl: data.eventLink,
                    eventURLExpiryDate: data.eventLinkExpiryDate ? new Date(data.eventLinkExpiryDate) : null,
                    isPasswordProtection: data.passwordProtection,
                    password: data.passwordProtection ? data.password : null
                };
                await dispatch(updateVDKEventLinkAndPasswordAsync(updateData)).unwrap();
                // Refresh event data
                if (eventId) {
                    dispatch(getVDKEventByIdForEventDetailsAsync({
                        id: eventId,
                        documentType: EventDocumentType.BackgroundImage
                    }));
                }
                toast.success(t('events.eventDetails.eventLinkAndPasswordUpdatedSuccessfully') ||
                    'Event link and password updated successfully!');
                setOpenEditDialog(false);
            }
            catch (error) {
                toast.error(error?.message ||
                    t('events.eventDetails.failedToUpdateEventLinkAndPassword') ||
                    'Failed to update event link and password. Please try again.');
            }
            finally {
                setIsSubmitting(false);
            }
        }
    };
    const moreMenuOptions = [
        // {
        //   text: t('events.eventDetails.exportData'),
        //   icon: <ExcelIcon color={theme.palette.primary.main} />,
        //   children: exportMenu
        // },
        {
            text: t('events.eventDetails.magicLink'),
            icon: _jsx(Link, { width: 20, height: 20 }),
            to: handleCopyMagicLink,
            disabled: !currentEvent?.eventURl
        }
    ];
    // Dialog content component
    const dialogContent = (_jsxs(Box, { padding: 2, gap: 1, display: 'flex', flexDirection: 'column', children: [editingField === 'labels.assignTo' && (_jsx(SingleSelect, { bg: 'grey8', name: 'assignTo', control: dialogControl, options: userOptions, label: t('labels.assignTo'), rules: {
                    required: t('validations.required', { field: t('labels.assignTo') })
                } })), editingField === 'labels.eventDuration' && (_jsx(Controller, { name: 'eventDuration', control: dialogControl, rules: {
                    required: t('events.dateRangeRequired'),
                    validate: (value) => {
                        if (!value?.startDate ||
                            !value?.endDate ||
                            value.startDate === '' ||
                            value.endDate === '') {
                            return t('events.dateRangeRequired');
                        }
                        return true;
                    }
                }, render: ({ field, fieldState }) => (_jsxs(Box, { sx: { cursor: 'pointer' }, children: [_jsx(HDateRangePicker, { bg: 'grey8', value: field.value, handleSelectedRange: (range) => range && field.onChange(range), label: t('labels.eventDuration'), readOnly: true }), fieldState.error && _jsx(FormValidationMessage, { error: fieldState.error.message || '' })] })) })), editingField === 'labels.company' && (_jsx(ControlledHCheckboxAutocomplete, { name: 'companies', control: dialogControl, bg: 'grey8', options: companyOptions, label: t('labels.company'), error: dialogErrors.companies?.message, rules: {
                    required: t('validations.required', { field: t('labels.company') }),
                    validate: (value) => {
                        if (!value || value.length === 0) {
                            return t('validations.required', {
                                field: t('labels.company')
                            });
                        }
                        return true;
                    }
                } })), editingField === 'labels.externalsAllowed' && (_jsx(SingleSelect, { name: 'externalsAllowed', label: t('labels.externalsAllowed'), options: externalsAllowedOptions, control: dialogControl, bg: 'grey8', error: dialogErrors.externalsAllowed?.message, rules: {
                    validate: (value) => {
                        if (value === null || value === undefined) {
                            return t('validations.required', {
                                field: t('labels.externalsAllowed')
                            });
                        }
                        return true;
                    }
                } })), editingField === 'labels.eventLinkAndPassword' && (_jsxs(_Fragment, { children: [_jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', control: dialogControl, name: 'eventLink', slotProps: {
                            input: {
                                readOnly: true,
                                endAdornment: (_jsx(IconButton, { onClick: handleCopyEventLink, size: 'small', sx: {
                                        border: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }, children: _jsx(Copy, { variant: 'Outline', fontSize: 24, style: {
                                            flexShrink: 0,
                                            width: 24,
                                            height: 24,
                                            color: theme.palette.action.active
                                        } }) }))
                            }
                        }, label: t('labels.eventLink') }), !isFamilyEvent && (_jsx(Controller, { name: 'eventLinkExpiryDate', control: dialogControl, render: ({ field, fieldState }) => (_jsxs(Box, { children: [_jsx(HSingleDatePicker, { bg: 'grey8', value: field.value, handleSelectedDate: (date) => field.onChange(date ?? ''), label: t('labels.eventLinkExpiryDate'), minDate: dayjs().startOf('day').toDate() }), fieldState.error && (_jsx(FormValidationMessage, { error: fieldState.error.message || '' }))] })) })), (!isFamilyEvent || isHappinessManager) && (_jsxs(_Fragment, { children: [_jsx(SingleSelect, { bg: 'grey8', control: dialogControl, name: 'passwordProtection', label: t('labels.passwordProtection'), options: passwordProtectionOptions, error: dialogErrors.passwordProtection?.message, rules: {
                                    validate: (value) => {
                                        if (value === null || value === undefined) {
                                            return t('validations.required', {
                                                field: t('labels.passwordProtection')
                                            });
                                        }
                                        return true;
                                    }
                                } }), isPasswordProtected && (_jsx(ControlledFilledTextField, { fullWidth: true, bg: 'grey8', control: dialogControl, name: 'password', type: showPassword ? 'text' : 'password', slotProps: {
                                    input: {
                                        endAdornment: (_jsx(IconButton, { disableTouchRipple: true, size: 'medium', disableFocusRipple: true, disableRipple: true, onClick: handleTogglePasswordVisibility, "aria-label": showPassword ? 'Hide password' : 'Show password', sx: {
                                                border: 'none',
                                                paddingLeft: 2,
                                                '&:hover': {
                                                    backgroundColor: 'transparent'
                                                }
                                            }, children: _jsx(Icon, { icon: showPassword ? 'solar:eye-closed-bold' : 'solar:eye-bold', fontSize: 24, color: theme.palette.grey[600], style: { flexShrink: 0, width: 24, height: 24 } }) }))
                                    }
                                }, label: t('labels.password'), placeholder: t('placeholders.setPasswordHere'), errorMessage: dialogErrors.password?.message, rules: {
                                    required: isPasswordProtected
                                        ? t('validations.required', { field: t('labels.password') })
                                        : false,
                                    minLength: isPasswordProtected
                                        ? {
                                            value: 6,
                                            message: t('validations.minLength', {
                                                field: t('labels.password'),
                                                min: 6
                                            })
                                        }
                                        : undefined,
                                    maxLength: isPasswordProtected
                                        ? {
                                            value: 20,
                                            message: t('validations.maxLength', {
                                                field: t('labels.password'),
                                                max: 20
                                            })
                                        }
                                        : undefined
                                } }))] }))] }))] }));
    const actionButtons = (_jsx(_Fragment, { children: _jsxs(Box, { display: 'flex', gap: 1, width: '100%', children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: handleCloseDialog, fullWidth: true, disabled: isSubmitting, children: t('actions.cancel') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', onClick: handleDialogSubmit(onDialogSubmit), disabled: isSubmitting, fullWidth: true, endIcon: _jsx(CheckOutlined, {}), children: t('actions.saveChanges') })] }) }));
    const dialogTitle = editingField ? t(editingField) : '';
    const handleDeleteFaqFromDetails = (faqId) => {
        const dispatchAction = isFamilyEvent
            ? dispatch(deleteFamilyFestFaqQuestionAsync(faqId))
            : dispatch(deleteFaqQuestionAsync({ id: faqId }));
        return dispatchAction
            .unwrap()
            .then((response) => {
            if (response?.isSuccess) {
                setSelectedFaqDetails(null);
                return true;
            }
            return false;
        })
            .catch(() => false);
    };
    const handleSaveFaqFromDetails = (payload) => {
        const request = {
            id: payload.id,
            question: payload.question,
            answer: payload.answer,
            sortOrder: payload.sortOrder,
            faqCategoryId: payload.faqCategoryId,
            translations: payload.translations
        };
        const dispatchAction = isFamilyEvent
            ? dispatch(updateFamilyFestFaqQuestionAsync(request))
            : dispatch(updateFaqQuestionAsync(request));
        return dispatchAction
            .unwrap()
            .then(() => {
            toast.success(t('events.eventDetails.faqTab.questionUpdatedSuccessfully'));
            // Keep the category rows' question counts right if the FAQ moved to another category.
            if (isFamilyEvent) {
                dispatch(getFamilyFestFaqCategoriesAsync());
            }
            else if (eventId) {
                dispatch(getFaqCategoriesByEventIdAsync({ eventId }));
            }
            return true;
        })
            .catch(() => {
            toast.error(t('events.eventDetails.faqTab.failedToUpdateQuestion'));
            return false;
        });
    };
    return (_jsxs(_Fragment, { children: [_jsx(PageLayout, { padding: true, title: isFaqDetailsView
                    ? t('events.eventDetails.faqTab.faqDetails') || 'FAQ Details'
                    : isEmailPreferencesSubView && emailPreferencesView.mode === 'settings'
                        ? t('events.eventDetails.preferencesTab.emailSettings')
                        : eventTitle || 'Event Details', showBackButton: true, showBreadCrumbs: true, customBreadcrumbs: breadcrumbs, onBackClick: () => {
                    if (isFaqDetailsView) {
                        setSelectedFaqDetails(null);
                        return;
                    }
                    if (isEmailPreferencesSubView) {
                        setEmailPreferencesView({ mode: 'list', template: null });
                        return;
                    }
                    if (locationId != null && eventId) {
                        const next = new URLSearchParams(searchParams);
                        next.delete('locationId');
                        setSearchParams(next, { replace: true });
                        return;
                    }
                    navigate(-1);
                }, rightContent: _jsx(_Fragment, { children: eventType !== EventTypeEnum.Director && (_jsxs(Box, { display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', children: [_jsxs(Box, { sx: {
                                    display: 'inline-flex',
                                    alignItems: 'stretch',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    backgroundColor: theme.palette.common.white,
                                    border: `1px solid ${theme.palette.grey[300]}`
                                }, children: [_jsx(Button, { variant: 'text', size: 'large', color: 'inherit', startIcon: _jsx(Icon, { icon: 'hugeicons:link-05', width: 20, height: 20 }), onClick: () => handleEditClick('labels.eventLinkAndPassword'), sx: {
                                            minWidth: 0,
                                            px: 2,
                                            borderRadius: 0,
                                            color: theme.palette.grey[900],
                                            backgroundColor: theme.palette.common.white,
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[100]
                                            }
                                        }, children: t('events.eventDetails.link') }), _jsx(Button, { variant: 'text', size: 'large', color: 'inherit', "aria-label": t('common.copy', { defaultValue: 'Copy link' }), onClick: handleCopyEventLink, disabled: !VDKEventsForEventDetails?.eventURl, sx: {
                                            minWidth: 56,
                                            px: 1.5,
                                            borderRadius: 0,
                                            borderLeft: `1px solid ${theme.palette.grey[300]}`,
                                            color: theme.palette.grey[900],
                                            backgroundColor: theme.palette.common.white,
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[100]
                                            }
                                        }, children: _jsx(Icon, { icon: 'hugeicons:copy-01', width: 24, height: 24 }) })] }), !isFamilyEvent && (_jsx(Button, { variant: 'white', size: 'large', color: 'inherit', startIcon: _jsx(Edit, {}), disabled: !canUpdateEvents || isCollaborator || isTransportManager, onClick: () => navigate(`/create-event?eventId=${eventId}`), children: t('tasks.edit') }))] })) }), showTabs: locationId == null && !isFaqDetailsView && !isEmailPreferencesSubView, tabs: _jsx(Tabs, { variant: 'scrollable', scrollButtons: 'auto', value: activeTab, onChange: (_, newValue) => setActiveTab(newValue), children: tabLabels.map(({ label, value, disabled, count }) => (_jsx(TabWithBadge, { badgeColor: label === t('events.eventDetails.responses')
                            ? theme.palette.primary.main
                            : undefined, badgeTextColor: label === t('events.eventDetails.responses')
                            ? theme.palette.common.white
                            : undefined, badgeCount: count, labelText: label, value: value, disabled: disabled }, value))) }), children: locationId != null ? (_jsx(LocationDetail, { locationId: locationId })) : isFaqDetailsView ? (_jsx(FaqDetailsContent, { faq: selectedFaqDetails?.faq, categoryName: selectedFaqDetails?.faq.faqCategoryId, onDeleteFaq: handleDeleteFaqFromDetails, onSaveFaq: handleSaveFaqFromDetails, onBack: () => setSelectedFaqDetails(null) })) : (_jsx(_Fragment, { children: _jsx(TabContext, { value: activeTab, children: _jsxs(Box, { sx: { ...styles.contentContainer }, children: [activeTab === 'details' && (_jsx(DetailsTab, { ref: detailsTabRef, onEditClick: handleEditClick })), activeTab === 'tasks' && _jsx(TasksTab, { isFamilyEvent: isFamilyEvent }), activeTab === 'attachments' && _jsx(AttachmentsTab, { isFamilyFest: isFamilyEvent }), activeTab === 'dashboard' &&
                                    (isFamilyEvent ? _jsx(FamilyFestDashboardTab, {}) : _jsx(DashboardTab, {})), !isExternalTransportManager && activeTab === 'responses' && (_jsx(ResponsesTab, { onAttendeesChanged: refreshFamilyFestCount })), !isExternalTransportManager && activeTab === 'rsvpTracking' && _jsx(RSVPTrackingTab, {}), !isExternalTransportManager && activeTab === 'translations' && _jsx(TranslationsTab, {}), !isExternalTransportManager && activeTab === 'interests' && _jsx(InterestsTab, {}), !isExternalTransportManager && activeTab === 'emailConfiguration' && (_jsx(PreferencesTab, { viewMode: emailPreferencesView.mode, selectedTemplate: emailPreferencesView.template, onOpenConfigure: (row) => setEmailPreferencesView({ mode: 'configure', template: row }), onOpenSettings: (row) => setEmailPreferencesView({ mode: 'settings', template: row }) })), activeTab === 'transport' && _jsx(TransportTab, {}), activeTab === 'accommodation' && _jsx(AccommodationTab, {}), activeTab === 'faqs' && _jsx(FaqsTab, { onOpenFaqDetails: setSelectedFaqDetails }), activeTab === 'eventPage' && isFamilyEvent && _jsx(EventPageTab, {})] }) }) })) }), !isMobile && (_jsx(CustomDialog, { open: openEditDialog, positionStatic: true, onClose: handleCloseDialog, titleIcon: '/assets/icons/star_header.png', titleWithIcon: dialogTitle, actions: actionButtons, children: dialogContent })), isMobile && (_jsx(BDrawer, { title: dialogTitle, showBottomBar: true, showCloseButton: true, open: openEditDialog, onClose: handleCloseDialog, children: _jsxs(Box, { sx: { flex: 1, overflow: 'auto' }, children: [dialogContent, _jsx(Box, { sx: { display: 'flex', gap: 2 }, children: actionButtons })] }) }))] }));
};
const EventDetails = () => {
    return (_jsx(FileUploadProvider, { children: _jsx(EventDetailsContent, {}) }));
};
export default EventDetails;
